/**
 * lib/nav-lang — ВЕТКА: виджет-механика выпадающего переключателя языка на рейле.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: ADR-0021 D-7 / T-I18N-2 — глобальный переключатель языка
 * живёт в singleton-рейле ПОД логотипом: триггер (флаг + нативное имя) → раскрытый
 * список 15 языков (флаг + эндоним каждый). КАРЕТНАЯ-механика раскрытия (открытие/
 * сворачивание, флип кареты, aria-expanded, Esc, outside-close, reduce-motion) — НЕ
 * собственная: она вынесена в ОБЩИЙ ствол `lib/disclosure-caret` (тикет fe-profile §4
 * Q4 / AC-23, owner-directive 2026-05-30 «ствол → ветки»). nav-lang и полоса ачивок
 * Профиля делят ОДИН ствол, не две копии. Эта ветка отвечает за ДАННЫЕ контрола (флаг
 * + эндоним + список, клавиатура-навигация по пунктам) — НЕ за механику раскрытия.
 *
 * SCOPE: построить контент контрола (флаг+имя в метку-триггера ствола + список 15
 *   пунктов в панель ствола), синхронизировать aria-selected/aria-current пунктов,
 *   обработать клавиатуру ВНУТРИ списка (стрелки/Enter/Home/End), фокус на активный
 *   пункт при раскрытии, обновить триггер/активный пункт при `setCurrent`. Раскрытие/
 *   сворачивание/Esc/outside-close/каретку держит `disclosure-caret`. NOT IN SCOPE:
 *   список языков (даёт рейл из config), смена store-locale / URL (колбэк `onSelect`),
 *   размеры/раскладка (CSS nav-lang.css + disclosure-caret.css), drawer рейла.
 *
 * **Флаг-логика рендера сохранена как была** (ветка branding несёт флаг США и пр.) —
 * рефактор меняет ТОЛЬКО механику раскрытия (теперь через disclosure-caret), не флаги.
 *
 * Cross-references: ADR-0021 D-7/D-8; T-I18N-2 AC-2/3/9/12; lib/disclosure-caret
 *   (ствол каретного раскрытия); lib/nav-rail (потребитель — строит options из config,
 *   проводит onSelect); responsive-ui §0.E (a11y popover); lib/autofit-text (fluid-
 *   подбор эндонима, чтобы длинный эндоним не обрезался — см. ниже).
 *
 * FLUID-ЭНДОНИМ (L0a 2026-06-25): эндонимы — ОДНА строка без переноса (owner 2026-06-19)
 * И без обрезки (§4.3 / §8.10). Длиннейший эндоним «Bahasa Indonesia» (id) шире
 * прежних подписей и при фикс-шрифте --fs-control вылезал за ширину пункта на ПК
 * (горизонтальный clip, assertNoHClip). Решение по правилам проекта (текст УМЕНЬШАЕТСЯ,
 * не обрезается): навешиваем общий модуль `attachAutofit` (mode:'line', capToDesign) —
 * шрифт эндонима = дизайн-размер, КОГДА влезает, и fluid-уменьшается до читаемого floor,
 * когда не влезает. Тот же приём «измеряем, не угадываем», что у autofit-кнопок рейла;
 * НЕ магик-кап, НЕ ellipsis, НЕ перенос.
 */

import { attachAutofit, type AutofitHandle } from './autofit-text.js';
import { mountDisclosureCaret, type DisclosureCaretHandle } from './disclosure-caret.js';
import { t } from './i18n/index.js';

/**
 * Floor читаемости эндонима на узком рейле (px). Передаётся autofit'у как `minPx`
 * (дефолт модуля = 6px — ниже обещанного floor). Длиннейший эндоним «Bahasa Indonesia»
 * (id) на тесном рейле уменьшается до этого предела, НЕ ниже — иначе нечитаем
 * (responsive-ui §0.A P1 / §8.6 floor читаемости). Не верхний кап (capToDesign — отдельно).
 */
const ENDONYM_MIN_PX = 13;

/** Один язык в списке контрола (данные строит рейл из config seo — единый источник). */
export interface NavLangOption {
  /** Код языка (`UI_LANGS`), напр. `ru` / `zh-CN`. На пункте → data-lang/lang/hreflang. */
  readonly code: string;
  /** Эндоним (нативное имя), напр. «Русский» / «简体中文» (`LANG_LABELS`). */
  readonly label: string;
  /** Готовая SVG-разметка флага (`lib/i18n/flags` по `LANG_FLAG`-коду). */
  readonly flagSvg: string;
  /** BCP-47 hreflang-тег (`HREFLANG_TAG`), напр. `zh-Hans`; на пункте → hreflang. */
  readonly hreflang: string;
}

export interface NavLangOptions {
  /** Языки для списка (порядок = `UI_LANGS`; рейл строит из config). */
  readonly options: readonly NavLangOption[];
  /** Текущий активный код языка (подсветка + триггер). */
  readonly current: string;
  /**
   * Код языка, ЗАКРЕПЛЁННЫЙ первой строкой списка (owner 2026-06-27) — это язык БРАУЗЕРА
   * (рейл считает его из navigator.language ОДИН раз). Фиксирован: при ручном выборе
   * другого языка порядок НЕ меняется (меняется только подсветка). Не задан → первый пункт.
   */
  readonly pinFirst?: string;
  /** Выбор языка (клик/Enter по пункту). Рейл проводит setLocale + navigateContent. */
  readonly onSelect: (code: string) => void;
}

export interface NavLangHandle {
  /** Корневой элемент контрола (.nav-lang) — рейл вставляет его под брендом. */
  readonly element: HTMLElement;
  /** Обновить активный язык (после смены): триггер + aria-current пунктов. */
  setCurrent(code: string): void;
  /** Закрыть список (рейл закрывает при навигации/закрытии drawer'а). */
  close(): void;
  /** Снять слушатели. */
  detach(): void;
}

/** HTML-escape для текстовых позиций (эндонимы — данные из config). */
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}

/**
 * Смонтировать контрол языка. Возвращает handle с корневым элементом (рейл сам решает,
 * куда его вставить — под брендом). Контрол НЕ трогает store/URL — выбор уходит в
 * `onSelect`; рейл проводит реактив (setLocale) + URL (navigateContent). Каретную
 * механику раскрытия несёт общий ствол `disclosure-caret` (AC-23).
 */
export function mountNavLang(opts: NavLangOptions): NavLangHandle {
  let current = opts.current;

  const cur = opts.options.find((o) => o.code === current) ?? opts.options[0]!;

  // ЯЗЫК БРАУЗЕРА — ФИКСИРОВАННАЯ первая строка списка (owner 2026-06-27): закрепляется
  // ОДИН раз и НЕ меняется при ручном выборе языка («каждый видит, что игра в первую
  // очередь под его страну»). pinFirst даёт рейл (navigator.language → UI_LANGS); fallback
  // — первый пункт. Активный язык лишь подсвечивается (setCurrent), порядок не трогает.
  const pinFirst =
    opts.pinFirst && opts.options.some((o) => o.code === opts.pinFirst)
      ? opts.pinFirst
      : opts.options[0]?.code ?? current;

  const optionEls = new Map<string, HTMLButtonElement>();

  // === Каретный disclosure-СТВОЛ (общий с полосой ачивок Профиля, AC-23) ===
  // .nav-lang — surface-класс на корне ствола (nav-lang.css вешает вид/ширину на него).
  const disclosure: DisclosureCaretHandle = mountDisclosureCaret({
    // Реактивность: nav-lang — singleton (рейл НЕ пере-монтирует его), но рейл зовёт
    // setCurrent() на каждой смене store.locale (nav-rail subscribe) → там же обновляем
    // aria-триггера на новый язык. t() при mount = язык первого рендера, setCurrent догоняет.
    ariaLabel: t('game.navLang.aria'),
    className: 'nav-lang',
    // surface-хуки на элементы ствола: триггер (тест кликает .nav-lang-trigger + CSS вид
    // триггера языка) и панель (= role=listbox-список). Показ/скрытие списка держит СТВОЛ
    // (.disclosure-caret--open .disclosure-caret-panel) — ветка НЕ заводит свой display-тоггл.
    triggerClassName: 'nav-lang-trigger',
    panelClassName: 'nav-lang-list',
    onToggle: (open) => {
      // При раскрытии — фокус на активный пункт (клавиатура, паритет прежнего поведения).
      if (open) {
        const active =
          optionEls.get(current) ?? list.querySelector<HTMLElement>('.nav-lang-option');
        active?.focus();
      }
    },
  });

  // --- Видимая метка триггера (флаг + нативное имя, must-visible, НЕ слово «Язык»).
  //     Флаг-разметка идентична прежней (branding-флаги сохранены). ---
  disclosure.labelHost.innerHTML = `
    <span class="nav-lang-trigger-flag" aria-hidden="true">${cur.flagSvg}</span>
    <span class="nav-lang-trigger-name">${esc(cur.label)}</span>
  `;

  // --- Список: панель ствола И ЕСТЬ role=listbox (.nav-lang-list — surface-хук панели,
  //     навешен через panelClassName). 15 пунктов (флаг + эндоним) кладём прямо в неё. ---
  const list = disclosure.panelHost;
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', t('game.navLang.listAria'));

  for (const o of opts.options) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'nav-lang-option';
    item.setAttribute('role', 'option');
    item.dataset.lang = o.code;
    item.setAttribute('lang', o.code);
    item.setAttribute('hreflang', o.hreflang);
    item.setAttribute('aria-selected', o.code === current ? 'true' : 'false');
    if (o.code === current) item.setAttribute('aria-current', 'true');
    item.innerHTML = `
      <span class="nav-lang-option-flag" aria-hidden="true">${o.flagSvg}</span>
      <span class="nav-lang-option-name">${esc(o.label)}</span>
    `;
    item.addEventListener('click', () => choose(o.code));
    list.appendChild(item);
    optionEls.set(o.code, item);
  }

  // ЯЗЫК БРАУЗЕРА (pinFirst) — ПЕРВОЙ строкой списка, остальные по приоритету рынков
  // (порядок opts.options = UI_LANGS). Закрепляется ОДИН раз; ручной выбор языка порядок
  // НЕ меняет (см. pinFirst выше). Триггер показывает текущий активный язык отдельно.
  function orderedCodes(first: string): readonly string[] {
    return [first, ...opts.options.map((o) => o.code).filter((c) => c !== first)];
  }
  function applyOrder(first: string): void {
    // appendChild перемещает существующий узел в конец → проход по orderedCodes выстраивает
    // DOM-порядок [first, ...рынки] без пересоздания пунктов.
    for (const code of orderedCodes(first)) {
      const el = optionEls.get(code);
      if (el) list.appendChild(el);
    }
  }
  applyOrder(pinFirst);

  // === Поведение: раскрытие/Esc/outside держит ствол; ВНУТРИ-списочную клавиатуру
  //     (стрелки/Enter/Home/End) держит ветка. ===
  function choose(code: string): void {
    disclosure.close();
    disclosure.trigger.focus();
    if (code !== current) opts.onSelect(code);
  }

  function onListKeydown(e: KeyboardEvent): void {
    // DOM-порядок (текущий язык вынесен первым через applyOrder), НЕ Map-порядок
    // optionEls — иначе стрелки навигировали бы не по видимому порядку.
    const items = Array.from(list.querySelectorAll<HTMLButtonElement>('.nav-lang-option'));
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[Math.min(items.length - 1, idx + 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[Math.max(0, idx - 1)]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if ((e.key === 'Enter' || e.key === ' ') && idx >= 0) {
      e.preventDefault();
      choose(items[idx]!.dataset.lang!);
    }
  }
  list.addEventListener('keydown', onListKeydown);

  // Триггер ствола: клавиатура (Enter/Space/стрелка-вниз открывают) — паритет прежнего.
  disclosure.trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      disclosure.open();
    }
  });

  function setCurrent(code: string): void {
    current = code;
    // Догоняем язык aria-триггера (вызывается из nav-rail на смене store.locale — reactive).
    disclosure.trigger.setAttribute('aria-label', t('game.navLang.aria'));
    const o = opts.options.find((x) => x.code === code);
    if (o) {
      const flagEl = disclosure.labelHost.querySelector<HTMLElement>('.nav-lang-trigger-flag');
      const nameEl = disclosure.labelHost.querySelector<HTMLElement>('.nav-lang-trigger-name');
      if (flagEl) flagEl.innerHTML = o.flagSvg;
      if (nameEl) nameEl.textContent = o.label;
    }
    for (const [c, el] of optionEls) {
      const active = c === code;
      el.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    }
    // Порядок списка НЕ трогаем при смене языка (pinFirst = язык браузера, фиксирован) —
    // меняется только подсветка активного. Эндоним триггера сменился → пере-подобрать шрифт.
    autofit.refit();
  }

  // FLUID-эндоним: подбираем шрифт эндонима под ширину пункта/триггера ИЗМЕРЕНИЕМ
  // (mode:'line' — весь эндоним в одну строку; capToDesign — не крупнее дизайн-размера
  // --fs-control, лишь уменьшается при нехватке ширины). Так длинный «Bahasa Indonesia»
  // не обрезается (§4.3/§8.10) и НЕ переносится (owner 2026-06-19). heightEl = сам
  // текст-элемент (у пункта/триггера высота стабильна от min-height в CSS). Пере-замер
  // на смену viewport/ширины рейла — встроен в attachAutofit (VIEWPORT_APPLIED_EVENT +
  // ResizeObserver). Список раскрыт не всегда: option-name меряется когда панель видима;
  // attachAutofit re-fit'ит на раскрытии через ResizeObserver панели.
  // minPx: ENDONYM_MIN_PX (13px) — floor читаемости эндонима (L0a-ревью): дефолт autofit'а
  // = 6px ниже обещанного floor, на узком рейле «Bahasa Indonesia» уходил бы нечитаемо мелким.
  // 13px держит читаемость; capToDesign — верхний потолок (дизайн-размер), не магик-кап.
  const autofit: AutofitHandle = attachAutofit(disclosure.element, [
    { selector: '.nav-lang-trigger-name', mode: 'line', capToDesign: true, minPx: ENDONYM_MIN_PX },
    { selector: '.nav-lang-option-name', mode: 'line', capToDesign: true, minPx: ENDONYM_MIN_PX },
  ]);

  return {
    element: disclosure.element,
    setCurrent,
    close: () => disclosure.close(),
    detach(): void {
      autofit.detach();
      list.removeEventListener('keydown', onListKeydown);
      disclosure.detach();
    },
  };
}
